import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  FieldTextInput,
  FieldSelect,
  FieldPhoneNumberInput,
  ProfileSettingsModal,
} from '../../components';

import css from './SignupForm.css';

const KEY_CODE_ENTER = 13;

const SignupFormComponent = props => {
  const { isOpen, onClose, onManageDisableScrolling, signupError } = props;

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          values,
          intl,
          onOpenTermsOfService,
        } = fieldRenderProps;

        // Email
        const emailLabel = intl.formatMessage({ id: 'SignupForm.emailLabel' });
        const emailPlaceholder = intl.formatMessage({ id: 'SignupForm.emailPlaceholder' });
        const emailRequiredMessage = intl.formatMessage({ id: 'SignupForm.emailRequired' });
        const emailRequired = validators.required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({ id: 'SignupForm.emailInvalid' });
        const emailValid = validators.emailFormatValid(emailInvalidMessage);
        const emailHelperText = intl.formatMessage({ id: 'SignupForm.emailHelperText' });

        // Password
        const passwordLabel = intl.formatMessage({ id: 'SignupForm.passwordLabel' });
        const passwordPlaceholder = intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' });
        const passwordRequiredMessage = intl.formatMessage({ id: 'SignupForm.passwordRequired' });
        const passwordMinLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooShort' },
          { minLength: validators.PASSWORD_MIN_LENGTH }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooLong' },
          { maxLength: validators.PASSWORD_MAX_LENGTH }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );

        // First Name
        const firstNameLabel = intl.formatMessage({ id: 'SignupForm.firstNameLabel' });
        const firstNamePlaceholder = intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' });
        const firstNameRequiredMessage = intl.formatMessage({ id: 'SignupForm.firstNameRequired' });
        const firstNameRequired = validators.required(firstNameRequiredMessage);

        // Last Name
        const lastNameLabel = intl.formatMessage({ id: 'SignupForm.lastNameLabel' });
        const lastNamePlaceholder = intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' });
        const lastNameRequiredMessage = intl.formatMessage({ id: 'SignupForm.lastNameRequired' });
        const lastNameRequired = validators.required(lastNameRequiredMessage);

        // Name validation - shared
        const nameTooShortMessage = intl.formatMessage(
          { id: 'SignupForm.nameTooShort' },
          { minLength: validators.NAME_MIN_LENGTH }
        );
        const nameRestrictedMessage = intl.formatMessage({ id: 'SignupForm.nameRestricted' });
        const nameInvalidCharactersMessage = intl.formatMessage({
          id: 'SignupForm.nameInvalidCharacters',
        });
        const nameMinLength = validators.minLength(nameTooShortMessage, validators.NAME_MIN_LENGTH);
        const nameNotRestricted = validators.nameRestricted(
          nameRestrictedMessage,
          validators.RESTRICTED_NAME_TERMS
        );
        const nameCharsValid = validators.validName(nameInvalidCharactersMessage);

        // Phone number
        const phoneNumberLabel = intl.formatMessage({ id: 'SignupForm.phoneNumberLabel' });
        const phoneNumberRequiredMessage = intl.formatMessage({
          id: 'SignupForm.phoneNumberRequired',
        });
        const phoneNumberRequired = validators.phoneNumberRequired(phoneNumberRequiredMessage);
        const phoneNumberHelperText = intl.formatMessage({
          id: 'SignupForm.phoneNumberHelperText',
        });

        // Account type
        const accountTypeLabel = intl.formatMessage({ id: 'SignupForm.accountTypeLabel' });
        const accountTypePlaceholder = intl.formatMessage({
          id: 'SignupForm.accountTypePlaceholder',
        });
        const accountRoleRequiredMessage = intl.formatMessage({
          id: 'SignupForm.accountRoleRequired',
        });
        const accountRoleRequired = validators.required(accountRoleRequiredMessage);
        const accountRoleFreelancerOption = intl.formatMessage({
          id: 'SignupForm.accountRoleFreelancerOption',
        });
        const accountRoleLightEntrepreneurOption = intl.formatMessage({
          id: 'SignupForm.accountRoleLightEntrepreneurOption',
        });
        const accountRoleCompanyAbroadOption = intl.formatMessage({
          id: 'SignupForm.accountRoleCompanyAbroadOption',
        });

        // How did you hear about us
        const howDidYouHearLabel = intl.formatMessage({ id: 'SignupForm.howDidYouHearLabel' });
        const howDidYouHearPlaceholder = intl.formatMessage({
          id: 'SignupForm.howDidYouHearPlaceholder',
        });
        const howDidYouHearRequiredMessage = intl.formatMessage({
          id: 'SignupForm.howDidYouHearRequired',
        });
        const howDidYouHearRequired = validators.required(howDidYouHearRequiredMessage);
        const howDidYouHearOtherLabel = intl.formatMessage({
          id: 'SignupForm.howDidYouHearOtherLabel',
        });
        const howDidYouHearOtherPlaceholder = intl.formatMessage({
          id: 'SignupForm.howDidYouHearOtherPlaceholder',
        });
        const howDidYouHearOtherRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.howDidYouHearOtherRequired' })
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const tosAccepted = values.tosAccepted;
        const submitDisabled = invalid || submitInProgress || !tosAccepted;

        const handleTermsKeyUp = e => {
          if (e.keyCode === KEY_CODE_ENTER) {
            onOpenTermsOfService();
          }
        };

        const termsLink = (
          <span
            className={css.termsLink}
            onClick={onOpenTermsOfService}
            role="button"
            tabIndex="0"
            onKeyUp={handleTermsKeyUp}
          >
            <FormattedMessage id="SignupForm.termsAndConditionsLinkText" />
          </span>
        );

        return (
          <ProfileSettingsModal
            id="SignupForm"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            title={intl.formatMessage({ id: 'SignupForm.title' })}
            onSubmit={handleSubmit}
            submitDisabled={submitDisabled}
            submitInProgress={submitInProgress}
            submitLabel={intl.formatMessage({ id: 'SignupForm.signUp' })}
            hideFooter
          >
            <Form className={classes} onSubmit={handleSubmit}>
              {signupError}
              <div className={css.formFields}>
                {/* First Name + Last Name */}
                <div className={css.nameRow}>
                  <FieldTextInput
                    rootClassName={css.field}
                    inputRootClass={css.customInput}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    validate={validators.composeValidators(
                      firstNameRequired,
                      nameMinLength,
                      nameNotRestricted,
                      nameCharsValid
                    )}
                    showSpacer={false}
                  />
                  <FieldTextInput
                    rootClassName={css.field}
                    inputRootClass={css.customInput}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    validate={validators.composeValidators(
                      lastNameRequired,
                      nameMinLength,
                      nameNotRestricted,
                      nameCharsValid
                    )}
                    showSpacer={false}
                  />
                </div>

                {/* Email */}
                <FieldTextInput
                  rootClassName={css.field}
                  inputRootClass={css.customInput}
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={emailLabel}
                  placeholder={emailPlaceholder}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  showSpacer={false}
                  infoText={emailHelperText}
                />

                {/* Password */}
                <FieldTextInput
                  rootClassName={css.field}
                  inputRootClass={css.customInput}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={passwordLabel}
                  placeholder={passwordPlaceholder}
                  validate={passwordValidators}
                  showSpacer={false}
                />

                {/* Phone number */}
                <div className={css.phoneField}>
                  <FieldPhoneNumberInput
                    type="text"
                    id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                    name="phoneNumber"
                    label={phoneNumberLabel}
                    validate={phoneNumberRequired}
                    infoText={phoneNumberHelperText}
                  />
                </div>

                {/* Account type */}
                <FieldSelect
                  rootClassName={css.fieldSelect}
                  id={formId ? `${formId}.accountRole` : 'accountRole'}
                  name="accountRole"
                  label={accountTypeLabel}
                  validate={accountRoleRequired}
                >
                  <option value="">{accountTypePlaceholder}</option>
                  <option value="freelancer">{accountRoleFreelancerOption}</option>
                  <option value="light-entrepreneur">{accountRoleLightEntrepreneurOption}</option>
                  <option value="company-abroad">{accountRoleCompanyAbroadOption}</option>
                </FieldSelect>

                {/* How did you hear about us */}
                <FieldSelect
                  rootClassName={css.fieldSelect}
                  id={formId ? `${formId}.howDidYouHear` : 'howDidYouHear'}
                  name="howDidYouHear"
                  label={howDidYouHearLabel}
                  validate={howDidYouHearRequired}
                >
                  <option value="">{howDidYouHearPlaceholder}</option>
                  <option value="referral">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearReferral' })}
                  </option>
                  <option value="ai">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearAI' })}
                  </option>
                  <option value="searchEngine">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearSearchEngine' })}
                  </option>
                  <option value="linkedin">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearLinkedin' })}
                  </option>
                  <option value="tiktok">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearTiktok' })}
                  </option>
                  <option value="instagram">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearInstagram' })}
                  </option>
                  <option value="facebook">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearFacebook' })}
                  </option>
                  <option value="x">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearX' })}
                  </option>
                  <option value="email">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearEmail' })}
                  </option>
                  <option value="phone">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearPhone' })}
                  </option>
                  <option value="event">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearEvent' })}
                  </option>
                  <option value="other">
                    {intl.formatMessage({ id: 'SignupForm.howDidYouHearOther' })}
                  </option>
                </FieldSelect>

                {values.howDidYouHear === 'other' ? (
                  <FieldTextInput
                    rootClassName={css.field}
                    inputRootClass={css.customInput}
                    type="text"
                    id={formId ? `${formId}.howDidYouHearOther` : 'howDidYouHearOther'}
                    name="howDidYouHearOther"
                    label={howDidYouHearOtherLabel}
                    placeholder={howDidYouHearOtherPlaceholder}
                    validate={howDidYouHearOtherRequired}
                    showSpacer={false}
                  />
                ) : null}
              </div>

              <Field name="tosAccepted" type="checkbox">
                {({ input }) => (
                  <label className={css.termsRow}>
                    <input className={css.termsCheckbox} type="checkbox" {...input} />
                    <span className={css.termsText}>
                      <FormattedMessage
                        id="SignupForm.termsAndConditionsAcceptText"
                        values={{ termsLink }}
                      />
                    </span>
                  </label>
                )}
              </Field>

              <div className={css.submitRow}>
                <button
                  type="submit"
                  className={submitDisabled ? css.submitButtonDisabled : css.submitButton}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="SignupForm.signUp" />
                </button>
              </div>
            </Form>
          </ProfileSettingsModal>
        );
      }}
    />
  );
};

SignupFormComponent.defaultProps = { inProgress: false };

const { bool, func } = PropTypes;

SignupFormComponent.propTypes = {
  inProgress: bool,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onOpenTermsOfService: func.isRequired,
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
